import React from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import {
    svgStroke, messageColors,
    belowDesktop
} from 'utilities/styled'
import { getFullName } from 'utilities/person'
import { linkCss } from 'components/typography/link'
import MessageBase from 'components/message'
import { H2, H4, h3Css } from 'components/typography/heading'
import { ExternalLink as ExternalLinkBase } from 'components/link'
import { Grid } from 'components/grid'
import {
    TableColumnHeader as TableColumnHeaderBase,
    TableRow as TableRowBase,
    TableRowHeader as TableRowHeaderBase,
    TableCell as TableCellBase
} from 'components/table'
import { Inline } from 'components/code'
import { UnorderedList as UnorderedListBase } from 'components/list'
import InstructionsBase from 'components/instructions'
import FeedbackQuestionsBase from 'app/layouts/private/help/feedback-questions'
import { CaptionSmall } from 'components/typography/caption'

export const Section = styled.div`
    &:where(:not(:last-of-type)) {
        margin-block-end: 40px;
    }

    & > :last-child {
        margin-block-end: 0;
    }
`

export const Message = styled(MessageBase)`
    margin-block-end: 24px;
`

export const WrappedText = styled.div`
    margin-block-end: 24px;
    border: 1px solid var(--huma-color-border-default);
    border-radius: 8px;
    padding: 24px;
`

export const ExternalLink = styled(ExternalLinkBase).attrs(attrs => ({
    ...attrs,
    target: '_blank'
}))`
    ${linkCss}

    display: inline;

    border: none;
    padding: 0;

    svg {
        margin-block-start: -4px;
        margin-inline-start: 4px;
        width: 16px;
        height: 16px;
    }
`

export const ExternalLinkBlock = styled(ExternalLinkBase).attrs(attrs => ({
    ...attrs,
    target: '_blank'
}))`
    ${linkCss}

    margin-block-end: 16px;
`

export const IconMessage = styled(Grid)`
    grid-template-columns: 20px 1fr;
    column-gap: 16px;

    ${messageColors}

    svg {
        ${svgStroke()}

        margin-block-start: 2px;
    }
`

export const Heading = styled(H2)`
    ${h3Css}

    margin-block-end: 16px;
`

export const Subheading = styled(H4)`
    font-size: 16px;
    margin-block-end: 12px;
`

/* To match the indent of a message in a list */
export const Indenter = styled.div`
    padding-inline-start: 36px;
`

export const FieldList = styled.div`
    margin-block-end: 24px;
    color: var(--huma-color-foreground-default);

    ol &,
    ul & {
        font-size: 14px;

        svg {
            ${svgStroke('small')}

            width: 20px;
            height: 20px;
        }
    }
`

export const FieldRow = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: auto;
    align-items: center;
    justify-content: start;

    box-shadow: 0 -1px var(--huma-color-border-default);
    padding: 16px 0;

    &.disabled {
        color: var(--huma-color-foreground-disabled);
    }

    &:last-of-type {
        box-shadow:
            0 -1px var(--huma-color-border-default),
            0 1px var(--huma-color-border-default);
    }

    svg {
        ${svgStroke()}

        margin-inline-end: 16px;
    }
`

export const TableColumnHeader = styled(TableColumnHeaderBase)`
    padding-bottom: 16px;
    color: var(--huma-color-foreground-default);
    font-weight: 500;
`

export const TableRow = TableRowBase

export const TableRowHeader = styled(TableRowHeaderBase)`
    padding: 16px 16px 16px 0;
    line-height: 24px;
    font-weight: normal;
    vertical-align: text-top;
    border: 1px solid var(--huma-color-border-default);
    border-width: 1px 0;

    > svg {
        ${svgStroke()}

        margin-inline-end: 16px;
    }
`

export const TableCell = styled(TableCellBase)`
    border: 1px solid var(--huma-color-border-default);
    border-width: 1px 0;
    padding: 16px 0;

    line-height: 24px;

    & :last-child {
        margin-block-end: 0;
    }
`

export const InlineCode = styled(Inline)`
    ${FieldRow} & {
        justify-self: start;
    }
`

export const CodeLine = styled(Inline)`
    display: block;

    width: max-content;
    padding-block: 0;

    &:not(:last-child) {
        margin-block: 0 2px;
    }
`

export const UnorderedList = styled(UnorderedListBase)`
    margin: 0 0 24px;
    padding-inline-start: 24px;
    list-style-type: disc;

    ul {
        margin-block-start: 8px;
        margin-block-end: 0;
    }

    & > li > ul {
        list-style-type: circle;

        & > li > ul {
            list-style-type: square;
        }
    }

    li {
        line-height: 20px;

        &:not(:last-child) {
            margin-block-end: 8px;
        }
    }
`

export const Summary = styled(Grid)`
    grid-auto-rows: auto;
    row-gap: 24px;
`

export const SummaryRow = styled(Grid)`
    grid-template-columns: 16px auto;
    column-gap: 8px;
    align-items: flex-start;

    font-size: 14px;
    line-height: 20px;

    svg {
        ${svgStroke()}
    }
`

export const Emoji = styled.span`
    font-size: 16px;
`

export const StatusIcon = styled.span`
    ${messageColors}
`

const By = styled.span`
    color: var(--huma-color-foreground-subtle);
`

export const Byline = ({ name }) => (
    <span>
        <FormattedMessage
            id="integration_message_connected_by"
            defaultMessage="<misty>By</misty> {name}"
            values={{
                misty: chunks => <By>{chunks}</By>,
                name: getFullName(name)
            }} />
    </span>
)

export const Instructions = styled(InstructionsBase)`
    /* margin-block-end: 0; */
`

export const FeedbackQuestions = styled(FeedbackQuestionsBase)`
    margin: 0;
    padding: 0;

    ${belowDesktop`
        order: 4;
    `}
`

export const InlineLoaderWrap = styled.div`
    height: 160px;
    position: relative;
`

export const Disclaimer = styled(CaptionSmall)`
    padding-block: 12px 0;

    &:where(:not(:last-child)) {
        padding-block: 12px;
    }

    &:where(.source-validation) {
        margin-block-start: -8px;
    }
`

export const BlockCodeWrapper = styled.div`
    max-width: 100%;
    overflow-x: hidden;
`